import AnimatedSection from "./AnimatedSection";
import { useLang } from "@/context/LanguageContext";
import translations from "@/data/translations";
import { MessageCircle, Info } from "lucide-react";

const WHATSAPP_URL = "https://wa.me/34672808030";

const PricingSection = () => {
  const { lang } = useLang();
  const t = translations[lang].pricing;

  return (
    <section id="pricing" data-testid="pricing-section" className="bg-slate-50 py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <AnimatedSection>
          <div className="text-center mb-6">
            <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-semibold text-electric mb-4 font-body">
              {t.overline}
            </p>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-navy font-heading mb-4">
              {t.headline}
            </h2>
            <p className="text-lg text-slate-500 font-body max-w-2xl mx-auto">{t.subheadline}</p>
          </div>
        </AnimatedSection>

        {/* Operation explanation */}
        <AnimatedSection delay={0.1}>
          <div data-testid="operation-note" className="max-w-2xl mx-auto mb-12 flex items-start gap-3 bg-electric/5 border border-electric/10 rounded-xl p-4">
            <Info size={18} className="text-electric shrink-0 mt-0.5" />
            <p className="text-slate-600 text-sm font-body">{t.operationNote}</p>
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
          {t.plans.map((plan, i) => (
            <AnimatedSection key={i} delay={0.15 + i * 0.08}>
              <div
                data-testid={`pricing-card-${i}`}
                className={`rounded-2xl p-7 h-full flex flex-col relative overflow-hidden transition-all duration-300 hover:-translate-y-1 ${
                  plan.highlighted
                    ? "bg-navy text-white shadow-2xl shadow-navy/30 ring-2 ring-electric/30"
                    : "bg-white border border-slate-200 hover:shadow-xl"
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute top-0 right-0 w-32 h-32 bg-electric/10 rounded-full -mr-12 -mt-12 blur-3xl" />
                )}
                <div className="relative z-10 flex-1">
                  <p className={`text-xs font-semibold uppercase tracking-wider mb-3 font-heading ${plan.highlighted ? "text-electric-light" : "text-electric"}`}>
                    {plan.name}
                  </p>
                  <p className={`text-sm mb-5 font-body ${plan.highlighted ? "text-slate-300" : "text-slate-500"}`}>
                    {plan.operations}
                  </p>
                  {plan.isCustom ? (
                    <p className={`text-2xl font-black font-heading mb-6 ${plan.highlighted ? "text-white" : "text-navy"}`}>
                      {t.customLabel}
                    </p>
                  ) : (
                    <div className="flex items-baseline gap-1 mb-6">
                      <span className={`text-4xl font-black font-heading ${plan.highlighted ? "text-white" : "text-navy"}`}>
                        {plan.price}
                      </span>
                      <span className={`text-lg font-body ${plan.highlighted ? "text-slate-300" : "text-slate-500"}`}>
                        {plan.suffix}
                      </span>
                    </div>
                  )}
                </div>
                <a
                  data-testid={`pricing-cta-${i}`}
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center justify-center gap-2 w-full font-semibold py-3 rounded-full transition-all duration-300 text-sm relative z-10 ${
                    plan.highlighted
                      ? "bg-electric text-white hover:bg-electric-hover shadow-lg shadow-electric/30"
                      : "bg-navy text-white hover:bg-navy-light"
                  }`}
                >
                  <MessageCircle size={15} />
                  {t.cta}
                </a>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
